import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { SimpleTree } from './ui/simple-growth-tree';
import { cn } from '../lib/utils';

const FiArrowRight = FiIcons.FiArrowRight;
const FiCheckCircle = FiIcons.FiCheckCircle;
const FiTrendingUp = FiIcons.FiTrendingUp;
const FiUsers = FiIcons.FiUsers;

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const achievements = [
    { icon: FiUsers, number: '500+', label: 'Farmers Trained' },
    { icon: FiTrendingUp, number: '7+', label: 'Years Experience' },
    { icon: FiCheckCircle, number: '95%', label: 'Success Rate' }
  ];

  return (
    <section id="home" className="relative min-h-screen overflow-hidden">
      {/* Tree Animation Background */}
      <div className="absolute inset-0 z-0">
        <SimpleTree />
      </div>
      
      {/* Semi-transparent overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-green-900/60 to-green-700/40 z-10"></div>
      
      {/* Content - Adjusted to position content lower to make room for the tree above */}
      <div className="relative z-20 flex items-center justify-center min-h-screen">
        <div className="container mx-auto px-4 pt-24 mt-40"> {/* Increased top margin from mt-32 to mt-40 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center text-white"
          >
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Cultivating Excellence in
              <span className="block text-yellow-400">Agricultural Workforce</span>
            </motion.h1>

            <motion.p 
              className="text-xl sm:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Transforming farms through people development. Professional consulting services 
              for skills development, governance advisory, and workforce training.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.button
                onClick={() => scrollToSection('services')}
                className="bg-yellow-500 text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-yellow-400 transition-all duration-300 flex items-center space-x-2 shadow-lg"
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Explore Services</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </motion.button>

              <motion.button
                onClick={() => scrollToSection('contact')}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-green-900 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Consultation
              </motion.button>
            </motion.div>

            {/* Achievements */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto bg-white/10 backdrop-blur-sm p-6 rounded-xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                >
                  <div className="flex justify-center mb-3">
                    <div className="bg-yellow-500/20 p-3 rounded-full">
                      <SafeIcon 
                        icon={achievement.icon} 
                        className="w-6 h-6 text-yellow-400" 
                      />
                    </div>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-1">
                    {achievement.number}
                  </div>
                  <div className="text-sm sm:text-base text-gray-300">
                    {achievement.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2"></div>
        </div>
      </motion.div>

      {/* Click to grow new tree hint */}
      <div className="absolute bottom-6 right-6 text-xs text-white/30 font-light z-20">
        Click background to grow a new tree
      </div>
    </section>
  );
};

export default Hero;